import React, { ChangeEvent } from 'react';
import { Stack, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import 'dayjs/locale/es';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { FormikTouched } from 'formik/dist/types';

type Props = {
    label: string;
    name: string;
    size?: 'small' | 'medium';
    margin?: 'dense' | 'normal' | 'none';
    required?: boolean;
    value?: any;
    touched?: boolean | FormikTouched<any>;
    errors?: string;
    minDate?: any;
    maxDate?: any;
    minTime?: any;
    maxTime?: any;
    disabled?: boolean;
    disablePast?: boolean;
    disableFuture?: boolean;
    onChange?: (value: ChangeEvent<HTMLInputElement> | null) => void;
    showCustomValueWhenDisabled?: boolean; // Show a custom value when the input is disabled and has no value
    customDisabledValue?: string; // Custom value to display (defaults to "N/A")
};

const DateTimePickerField = ({
    label,
    name,
    touched,
    errors,
    minDate,
    maxDate,
    minTime,
    maxTime,
    disabled,
    disablePast = false,
    disableFuture = false,
    onChange,
    required,
    size,
    value,
    showCustomValueWhenDisabled = false,
    customDisabledValue = 'N/A',
}: Props) => {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Stack component="div">
                {showCustomValueWhenDisabled && disabled && !value ? (
                    <TextField
                        label={label}
                        value={customDisabledValue}
                        disabled
                        fullWidth
                        size={size}
                        InputProps={{
                            sx: { color: 'gray' },
                        }}
                    />
                ) : (
                    <DateTimePicker
                        label={label}
                        value={value || null}
                        disabled={disabled}
                        disablePast={disablePast}
                        minDate={minDate}
                        maxDate={maxDate}
                        disableFuture={disableFuture}
                        ampm={false}
                        minTime={minTime}
                        maxTime={maxTime}
                        onChange={onChange}
                        slotProps={{
                            textField: {
                                name,
                                size,
                                required,
                                error: (touched && Boolean(errors)) || false,
                                helperText: touched ? errors : '',
                            },
                        }}
                        viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                        }}
                        sx={{
                            '& label.Mui-focused': { color: 'primary.main' },
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                },
                            },
                        }}
                    />
                )}
            </Stack>
        </LocalizationProvider>
    );
};

export default DateTimePickerField;
